/**
 * Inline SVG charts — chrome on white aesthetic.
 * All data is real and sourced; do not edit numbers without updating the
 * sources list in Sustainability.jsx.
 */

const CHROME_GRADIENT_ID = "dcyb-chrome-stroke";
const CHROME_FILL_ID = "dcyb-chrome-fill";

function Defs() {
  return (
    <defs>
      <linearGradient id={CHROME_GRADIENT_ID} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0%" stopColor="#0F1117" />
        <stop offset="50%" stopColor="#888B92" />
        <stop offset="100%" stopColor="#0F1117" />
      </linearGradient>
      <linearGradient id={CHROME_FILL_ID} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#2A2D34" />
        <stop offset="100%" stopColor="#9CA0A6" />
      </linearGradient>
    </defs>
  );
}

/* ============================================================
 * PUE line chart (Uptime Institute Global Data Center Survey)
 * ============================================================ */
const PUE_DATA = [
  { year: 2007, pue: 2.5 },
  { year: 2011, pue: 1.98 },
  { year: 2013, pue: 1.65 },
  { year: 2018, pue: 1.58 },
  { year: 2020, pue: 1.59 },
  { year: 2022, pue: 1.55 },
  { year: 2023, pue: 1.58 },
  { year: 2024, pue: 1.56 },
  { year: 2026, pue: 1.56 },
];

export function PUELineChart({ className = "" }) {
  const W = 640;
  const H = 280;
  const PAD = { top: 24, right: 24, bottom: 36, left: 44 };
  const yMin = 1.0;
  const yMax = 2.8;
  const xMin = 2007;
  const xMax = 2026;

  const xScale = (year) =>
    PAD.left + ((year - xMin) / (xMax - xMin)) * (W - PAD.left - PAD.right);
  const yScale = (pue) =>
    PAD.top + (1 - (pue - yMin) / (yMax - yMin)) * (H - PAD.top - PAD.bottom);

  const path = PUE_DATA.map(
    (d, i) => `${i ? "L" : "M"} ${xScale(d.year)} ${yScale(d.pue)}`
  ).join(" ");

  const areaPath =
    path +
    ` L ${xScale(xMax)} ${H - PAD.bottom} L ${xScale(xMin)} ${H - PAD.bottom} Z`;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label="PUE trend">
      <Defs />
      {/* Y grid */}
      {[1.0, 1.5, 2.0, 2.5].map((y) => (
        <g key={y}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={yScale(y)}
            y2={yScale(y)}
            stroke="#E3E5E9"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={PAD.left - 10}
            y={yScale(y) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#71747B"
            fontFamily="Space Mono, monospace"
          >
            {y.toFixed(1)}
          </text>
        </g>
      ))}
      {/* X labels */}
      {[2007, 2011, 2015, 2019, 2024].map((year) => (
        <text
          key={year}
          x={xScale(year)}
          y={H - PAD.bottom + 20}
          textAnchor="middle"
          fontSize="10"
          fill="#71747B"
          fontFamily="Space Mono, monospace"
        >
          {year}
        </text>
      ))}
      {/* Axis lines */}
      <line
        x1={PAD.left}
        y1={H - PAD.bottom}
        x2={W - PAD.right}
        y2={H - PAD.bottom}
        stroke="#0F1117"
        strokeWidth="1"
        vectorEffect="non-scaling-stroke"
      />
      {/* Area under curve */}
      <path d={areaPath} fill={`url(#${CHROME_FILL_ID})`} opacity="0.08" />
      {/* Line */}
      <path
        d={path}
        fill="none"
        stroke={`url(#${CHROME_GRADIENT_ID})`}
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
      {/* Points */}
      {PUE_DATA.map((d) => (
        <g key={d.year}>
          <circle cx={xScale(d.year)} cy={yScale(d.pue)} r="4" fill="#0F1117" />
          <circle cx={xScale(d.year)} cy={yScale(d.pue)} r="2" fill="#fff" />
        </g>
      ))}
      {/* End label */}
      <text
        x={xScale(2026) - 6}
        y={yScale(1.56) - 10}
        textAnchor="end"
        fontSize="11"
        fontWeight="700"
        fill="#0F1117"
        fontFamily="Orbitron, sans-serif"
      >
        1.56
      </text>
      <text
        x={xScale(2007) + 8}
        y={yScale(2.5) - 8}
        textAnchor="start"
        fontSize="11"
        fontWeight="700"
        fill="#0F1117"
        fontFamily="Orbitron, sans-serif"
      >
        2.50
      </text>
    </svg>
  );
}

/* ============================================================
 * Global DC capacity buildout — bar chart (JLL Global DC Outlook 2026)
 * Values are global installed DC capacity in GW. Years 2026 onward
 * are JLL projections en route to ~200 GW by 2030.
 * ============================================================ */
const ENERGY_DATA = [
  { year: 2020, twh: 60, projected: false },
  { year: 2022, twh: 75, projected: false },
  { year: 2024, twh: 95, projected: false },
  { year: 2026, twh: 110, projected: true },
  { year: 2028, twh: 155, projected: true },
  { year: 2030, twh: 200, projected: true },
];

export function EnergyBarChart({ className = "" }) {
  const W = 640;
  const H = 280;
  const PAD = { top: 28, right: 24, bottom: 36, left: 48 };
  const yMax = 220;
  const barW = (W - PAD.left - PAD.right) / ENERGY_DATA.length - 14;

  const yScale = (v) =>
    PAD.top + (1 - v / yMax) * (H - PAD.top - PAD.bottom);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label="Global DC capacity buildout">
      <Defs />
      {/* Grid */}
      {[0, 50, 100, 150, 200].map((g) => (
        <g key={g}>
          <line
            x1={PAD.left}
            x2={W - PAD.right}
            y1={yScale(g)}
            y2={yScale(g)}
            stroke="#E3E5E9"
            strokeWidth="1"
            vectorEffect="non-scaling-stroke"
          />
          <text
            x={PAD.left - 10}
            y={yScale(g) + 4}
            textAnchor="end"
            fontSize="10"
            fill="#71747B"
            fontFamily="Space Mono, monospace"
          >
            {g}
          </text>
        </g>
      ))}
      {/* Bars */}
      {ENERGY_DATA.map((d, i) => {
        const x =
          PAD.left + i * ((W - PAD.left - PAD.right) / ENERGY_DATA.length) + 8;
        const y = yScale(d.twh);
        const h = H - PAD.bottom - y;
        return (
          <g key={d.year}>
            <rect
              x={x}
              y={y}
              width={barW}
              height={h}
              fill={d.projected ? "url(#" + CHROME_FILL_ID + ")" : "#0F1117"}
              opacity={d.projected ? "0.45" : "1"}
            />
            {d.projected && (
              <rect
                x={x}
                y={y}
                width={barW}
                height={h}
                fill="none"
                stroke="#0F1117"
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            )}
            <text
              x={x + barW / 2}
              y={y - 8}
              textAnchor="middle"
              fontSize="10"
              fontWeight="700"
              fill="#0F1117"
              fontFamily="Orbitron, sans-serif"
            >
              {d.twh}
            </text>
            <text
              x={x + barW / 2}
              y={H - PAD.bottom + 20}
              textAnchor="middle"
              fontSize="10"
              fill="#71747B"
              fontFamily="Space Mono, monospace"
            >
              {d.year}
              {d.projected ? "e" : ""}
            </text>
          </g>
        );
      })}
      {/* Y label */}
      <text
        x={12}
        y={PAD.top + 4}
        fontSize="10"
        fill="#71747B"
        fontFamily="Space Mono, monospace"
      >
        GW
      </text>
    </svg>
  );
}

/* ============================================================
 * Cooling efficiency — horizontal bars
 * Relative facility energy vs legacy raised-floor air @ 100
 * Source: ASHRAE TC 9.9 + industry benchmarks (Lawrence Berkeley, Uptime)
 * ============================================================ */
const COOLING_DATA = [
  { label: "Air (legacy raised-floor)", value: 100 },
  { label: "Air (free / economizer)", value: 32 },
  { label: "Liquid cold-plate", value: 65 },
  { label: "Direct-to-chip", value: 55 },
  { label: "Immersion", value: 48 },
];

export function CoolingBars({ className = "" }) {
  const W = 640;
  const H = 260;
  const PAD = { top: 16, right: 56, bottom: 16, left: 200 };
  const rowH = (H - PAD.top - PAD.bottom) / COOLING_DATA.length;
  const max = 100;
  const xMax = W - PAD.right;
  const xMin = PAD.left;
  const xScale = (v) => xMin + (v / max) * (xMax - xMin);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className={className} role="img" aria-label="Cooling efficiency comparison">
      <Defs />
      {/* Vertical reference lines */}
      {[0, 25, 50, 75, 100].map((v) => (
        <line
          key={v}
          x1={xScale(v)}
          x2={xScale(v)}
          y1={PAD.top}
          y2={H - PAD.bottom}
          stroke="#E3E5E9"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {COOLING_DATA.map((d, i) => {
        const y = PAD.top + i * rowH + rowH / 2;
        const isLegacy = d.label.startsWith("Air (legacy");
        return (
          <g key={d.label}>
            <text
              x={PAD.left - 12}
              y={y + 4}
              textAnchor="end"
              fontSize="11"
              fill="#0F1117"
              fontFamily="Outfit, sans-serif"
              fontWeight="500"
            >
              {d.label}
            </text>
            <rect
              x={xScale(0)}
              y={y - 9}
              width={xScale(d.value) - xScale(0)}
              height={18}
              fill={isLegacy ? "#0F1117" : "url(#" + CHROME_FILL_ID + ")"}
            />
            <text
              x={xScale(d.value) + 8}
              y={y + 4}
              fontSize="11"
              fontWeight="700"
              fill="#0F1117"
              fontFamily="Orbitron, sans-serif"
            >
              {d.value}
            </text>
          </g>
        );
      })}
      {/* X-axis bottom labels */}
      {[0, 50, 100].map((v) => (
        <text
          key={v}
          x={xScale(v)}
          y={H - 2}
          textAnchor="middle"
          fontSize="9"
          fill="#71747B"
          fontFamily="Space Mono, monospace"
        >
          {v}
        </text>
      ))}
    </svg>
  );
}
